import { createFileRoute, notFound } from "@tanstack/react-router"
import Category from "@/pages/category"
import type { HttpTypes } from "@medusajs/types"
import { sanitize } from "@/lib/utils/sanitize"
import {
  PREORDER_ENABLED,
  SITE_NAME,
} from "@/lib/constants/site"
import { SITE_URL } from "@/lib/constants/site-url"
import {
  isCateringCategoryHandle,
  isExpiredCampaignCategoryHandle,
} from "@/lib/utils/catalog-visibility"
import { jsonLd } from "@/lib/utils/json-ld"

const OG_IMAGE = `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`

export const Route = createFileRoute("/$countryCode/categories/$handle")({
  loader: async ({ params, context }) => {
    const { countryCode, handle } = params

    // Pre-order is hidden site-wide (PREORDER_ENABLED) — 404 every category
    // page, since they only exist to browse the shop.
    if (!PREORDER_ENABLED) {
      throw notFound()
    }

    const { queryClient } = context

    // Catering visibility is a runtime toggle (Admin → Settings → Store) —
    // 404 catering category pages while the section is hidden.
    const { siteSettingsQueryOptions, isCateringVisible } = await import(
      "@/lib/data/site-settings"
    )
    const siteSettings = await queryClient.ensureQueryData(
      siteSettingsQueryOptions
    )
    if (!isCateringVisible(siteSettings) && isCateringCategoryHandle(handle)) {
      throw notFound()
    }
    const [{ getRegion }, { retrieveCategory }] = await Promise.all([
      import("@/lib/data/regions"),
      import("@/lib/data/categories"),
    ])

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region || !handle || isExpiredCampaignCategoryHandle(handle)) {
      throw notFound()
    }

    const category = await queryClient.ensureQueryData({
      queryKey: ["category", handle],
      queryFn: async () => {
        try {
          return await retrieveCategory({ handle })
        } catch {
          throw notFound()
        }
      },
    })

    return sanitize({
      countryCode,
      region,
      category: category as HttpTypes.StoreProductCategory,
    })
  },
  head: ({ loaderData }) => {
    const { category } = loaderData || {}
    const categoryName = category?.name || "Category"
    const categoryDesc =
      category?.description ||
      `Browse our ${categoryName.toLowerCase()} — freshly baked in Rancho Cucamonga, CA. Pre-order online for pickup or order same-day via Toast.`
    const title = `${categoryName} | ${SITE_NAME} — Rancho Cucamonga`
    const description = categoryDesc.length > 160 ? `${categoryDesc.slice(0, 157)}...` : categoryDesc
    const canonical = `${SITE_URL}/categories/${category?.handle || ""}`

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Menu", item: `${SITE_URL}/menu` },
        { "@type": "ListItem", position: 3, name: categoryName, item: canonical },
      ],
    }

    const collectionSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: categoryName,
      description,
      url: canonical,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:site_name", content: SITE_NAME },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
        { type: "application/ld+json", children: jsonLd(collectionSchema) },
      ],
    }
  },
  component: Category,
})
